import React, { JSX,
    CSSProperties,
    useEffect,
    useRef
} from "react";
import _ from "lodash";

import { normalizeClassName } from "../../render/renderUtils";
import {
    NoteActivityObserver,
    NoteActivityType,
} from "../../audio/NoteActivity";
import { NoteRangeType } from "../../model/containers";
import {
    Note,
    Tone
} from "../../model/core";
import './PianoKeybed.css';
import { RydTrack } from "../../model/tracks";
import { Tonality } from "../../model/scales";


export const isBlackKey = (note: Note | Tone) => note.getName().includes("#");

export type KeybedOrientationType = 'pianoroll' | 'keyboard';

/**
 * Component representing the keybed of a piano roll.
 *
 * @param {Object} props - The properties object.
 * @param {Array<number>} props.noteRange - An array containing the start and end notes of the range.
 * @param {boolean} [props.showOctave=true] - A flag indicating whether to show the octave numbers.
 * @param {string} [props.orientation='pianoroll'] - The orientation of the piano roll. Accepts 'pianoroll' or 'keyboard'.
 * @returns {JSX.Element} The rendered component.
 */
export function PianoRollKeybed({
    track,
    noteRange,
    showOctave = true,
    orientation = 'pianoroll',
    tonality,
    noteActivityObserver,
    stylingSource,
    triggerCB,
    style
}: {
    track?: RydTrack,
    noteRange: NoteRangeType,
    showOctave?: boolean,
    orientation?: KeybedOrientationType,
    tonality?: Tonality,
    noteActivityObserver?: NoteActivityObserver,
    stylingSource?: (n: Note) => string[],
    triggerCB?: (n: Note, ne: NoteActivityType) => void,
    style?: CSSProperties
}) {

    let classes = ["piano-roll-header"];
    classes = classes.concat(orientation === 'pianoroll' ? "piano-roll-header--pianoroll" : "piano-roll-header--keyboard");
    return (<div
        className={normalizeClassName(classes)}
        style={style}>{
            (orientation === 'pianoroll' ? _.rangeRight : _.range)(noteRange.min, noteRange.max)
                .map(Note.fromIndex)
                .map((n) => <KeybedNote
                    key={n.index}
                    track={track}
                    note={n}
                    showOctave={showOctave}
                    tonality={tonality}
                    noteActivityObserver={noteActivityObserver}
                    stylingSource={stylingSource}
                    triggerCB={triggerCB} />
                )}
    </div>);
}

export function RydIOKeybed({
    track,
    noteRange = { min: 36, max: 36 + (3 * 12) + 1 },
    showOctave = true,
    tonality,
    noteActivityObserver,
    stylingSource,
    triggerCB
}: {
    track?: RydTrack,
    noteRange?: NoteRangeType,
    showOctave?: boolean,
    tonality?: Tonality,
    noteActivityObserver?: NoteActivityObserver,
    stylingSource?: (n: Note) => string[],
    triggerCB?: (n: Note, type: NoteActivityType) => void
}) {
    return (<PianoRollKeybed
        track={track}
        noteRange={noteRange}
        showOctave={showOctave}
        orientation={'keyboard'}
        tonality={tonality}
        noteActivityObserver={noteActivityObserver}
        stylingSource={stylingSource}
        triggerCB={triggerCB}
    />);
}

export function RydToneMultiSelectKeybed({
    tones,
    setSelectedTones,
    stylingSource
} :{
    tones: Tone[],
    setSelectedTones: (tones: Tone[]) => void,
    stylingSource: (n: Tone) => 'in' | 'out' | 'center'
}) : JSX.Element{
    // const [tones, setTones] = React.useState<Tone[]>([]);
    return (<RydIOKeybed
        noteRange={{ min: 0, max: 12 }}
        showOctave={false}
        triggerCB={(n, e) => {
            if (e.event === 'noteoff') return;
            const newTones = (tones.includes(n.getTone())
                ? tones.filter(t => t !== n.getTone())
                : [...tones, n.getTone()]
                .sort((a, b) => a.index - b.index));
            setSelectedTones(newTones);
        }}
        stylingSource={(note: Note) => {
            // console.log('stylingSource for ', note.getTone().getName(), ' is ', stylingSource(note.getTone()));
            switch (stylingSource(note.getTone())) {
                case 'in': return ['piano-roll--key-selected'];
                case 'out': return [];
                case 'center': return ['piano-roll--tonality--center'];
            }
        }}
    />);
}

function KeybedNote({
    track,
    note,
    showOctave,
    tonality,
    noteActivityObserver,
    stylingSource,
    triggerCB
}: {
    track?: RydTrack,
    note: Note,
    showOctave: boolean,
    tonality?: Tonality,
    noteActivityObserver?: NoteActivityObserver,
    stylingSource?: (n: Note) => string[],
    triggerCB?: (n: Note, type: NoteActivityType) => void
}) {
    const [listenerId, setListenerId] = React.useState<number>();
    const noteKeyEl = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!noteActivityObserver) return;
        if (!track) return;
        noteActivityObserver.listenForNoteSpecificEvent(track.id, note.index, (activityType: NoteActivityType) => {

            if (!noteKeyEl.current) return;
            if (activityType.event === 'noteon') {
                noteKeyEl.current.classList.add('pr-key--note-sounding');
            } else {
                noteKeyEl.current.classList.remove('pr-key--note-sounding');
            }
        });

        return () => {
            noteActivityObserver.dropNoteListener(track.id, note.index);
        };
    }, [note, noteActivityObserver]);

    let classes = ["piano-roll-header-note"];
    let classesWrapper = ["keybed--key-wrapper"];
    classes = classes.concat(isBlackKey(note) ? "piano-roll--black-key" : "piano-roll--white-key");
    if (note.getTone().getName() === "E" || note.getTone().getName() === "B") {
        classes = classes.concat("piano-roll--white-key--border-highlight");
    }
    if (tonality) {
        classesWrapper = classesWrapper.concat(tonality.containsNote(note) ? "piano-roll--tonality-note" : "piano-roll--non-tonality-note");
        classesWrapper = classesWrapper.concat(tonality.center.index === note.index % 12 ? "piano-roll--tonality--center" : '');
    }
    classesWrapper = classesWrapper.concat(stylingSource ? stylingSource(note) : []);

    const noteLabel = (showOctave ? note : note.getTone()).getName();
    return (
        <div className={normalizeClassName(classesWrapper)}>
            <div className={"keybed--note-label"}>{noteLabel}</div>
            <div
                ref={noteKeyEl}
                className={normalizeClassName(classes)}
                onMouseDown={(e) => triggerCB?.(note, { event: "noteon" })}
                onMouseUp={(e) => triggerCB?.(note, { event: "noteoff" })}
            />
        </div>
    );
}
